import type { LinkProps } from 'react-router-dom';

/**
 * The inert-control recipe Button and IconButton share (#1052 review 1), so
 * what one refuses the other refuses: one click guard, one set of props for
 * a link that cannot be followed, one split of the router-only props.
 */

type AriaDisabled = boolean | 'true' | 'false' | undefined;

export interface InertInput {
  disabled?: boolean;
  loading?: boolean;
  /** The caller's aria-disabled: "true" refuses like disabled, but keeps the tab stop. */
  ariaDisabled?: AriaDisabled;
}

/**
 * `inert`: the control refuses (disabled, busy, or aria-disabled="true").
 * `blocked`: it refuses but keeps its tab stop (busy or aria-disabled), so the
 * person who just pressed it does not lose their place.
 */
export function inertState({ disabled = false, loading = false, ariaDisabled }: InertInput) {
  const blocked = loading || ariaDisabled === true || ariaDisabled === 'true';
  return { inert: disabled || blocked, blocked };
}

/**
 * The props an <a> takes for its state. A link has no `disabled` attribute,
 * so one that cannot be followed drops its href entirely (no middle click,
 * ctrl click or context menu opens it), keeps its link role explicitly and
 * says aria-disabled; a disabled one leaves the tab order, a busy or
 * aria-disabled one keeps its tab stop (0, or the caller's).
 */
export function inertAnchorProps({
  href,
  role,
  tabIndex,
  ...state
}: InertInput & { href?: string; role?: string; tabIndex?: number }) {
  const { inert, blocked } = inertState(state);
  return {
    href: inert ? undefined : href,
    role: inert ? 'link' : role,
    'aria-disabled': inert || undefined,
    'aria-busy': state.loading || undefined,
    tabIndex: state.disabled ? -1 : blocked ? (tabIndex ?? 0) : tabIndex,
  };
}

/** Swallows the click of an inert control (and its default); passes a live one on. */
export function clickGuard<E extends { preventDefault: () => void }>(
  inert: boolean,
  onClick?: (event: E) => void,
) {
  return (event: E) => {
    if (inert) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
}

const ROUTER_KEYS = [
  'to',
  'discover',
  'prefetch',
  'reloadDocument',
  'replace',
  'state',
  'preventScrollReset',
  'relative',
  'viewTransition',
] as const;

type RouterKey = (typeof ROUTER_KEYS)[number];
export type RouterProps = Partial<Pick<LinkProps, RouterKey>>;

/**
 * Splits the router Link's own props off the rest, so an inert link (which
 * draws a plain <a>) passes none of them to the DOM, and a live one passes
 * them all to the Link in one object. Absent props stay absent.
 */
export function splitRouterProps<T extends object>(props: T) {
  const router: Record<string, unknown> = {};
  const dom: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if ((ROUTER_KEYS as readonly string[]).includes(key)) router[key] = value;
    else dom[key] = value;
  }
  return { router: router as RouterProps, dom: dom as Omit<T, RouterKey> };
}
